/**
 * /posvenda/assistencias/kanban — Kanban visual com 1 coluna por situacao.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiAlertTriangle, FiList, FiRefreshCw } from "react-icons/fi";

import EntityHeader from "../../../components/EntityHeader";
import { useAuth } from "../../../context/AuthContext";
import {
  formatarHorasRestantes,
  listarAssistencias,
  PRIORIDADE_LABELS,
  PRIORIDADE_TONES,
  SITUACAO_LABELS,
  SITUACAO_TONES,
  SITUACOES_KANBAN,
} from "../../../services/posvenda";
import { PageContainer } from "../../../styles/shared";

import { fmtData } from "../../Orcamentos/helpers";
import { ErrorMsg, Pill } from "../../Orcamentos/styles";


export default function AssistenciasKanban() {
  const navigate = useNavigate();
  const { request } = useAuth();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = await listarAssistencias(request);
      setItems(Array.isArray(payload) ? payload : payload?.results || []);
    } catch (err) {
      setError(err?.message || "Erro ao carregar.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [request]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const colunas = useMemo(() => {
    const map = {};
    SITUACOES_KANBAN.forEach((s) => { map[s] = []; });
    items.forEach((a) => {
      if (map[a.situacao]) map[a.situacao].push(a);
    });
    return map;
  }, [items]);

  return (
    <PageContainer>
      <EntityHeader
        title="Kanban de assistências"
        subtitle="Visualização por situação (ADR-0027)"
        actions={[
          {
            label: "Ver lista",
            icon: <FiList size={14} aria-hidden="true" />,
            onClick: () => navigate("/posvenda/assistencias"),
          },
          {
            label: "Atualizar",
            icon: <FiRefreshCw size={14} aria-hidden="true" />,
            onClick: carregar,
          },
        ]}
      />

      {error && <ErrorMsg><FiAlertTriangle size={14} /> {error}</ErrorMsg>}

      <div style={{
        display: "grid",
        gridTemplateColumns: `repeat(${SITUACOES_KANBAN.length}, minmax(220px, 1fr))`,
        gap: 12,
        overflowX: "auto",
      }}>
        {SITUACOES_KANBAN.map((s) => (
          <div
            key={s}
            style={{
              background: "var(--surfaceAlt, #f4f4f5)",
              borderRadius: 8,
              padding: 8,
              minHeight: 200,
            }}
          >
            <div style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              marginBottom: 8,
              padding: "4px 8px",
            }}>
              <strong style={{ fontSize: 13 }}>
                {SITUACAO_LABELS[s] || s}
              </strong>
              <Pill $tone={SITUACAO_TONES[s] || "neutral"} style={{ fontSize: 11 }}>
                {colunas[s]?.length || 0}
              </Pill>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {colunas[s]?.length === 0 && !loading && (
                <div style={{ fontSize: 12, opacity: 0.5, padding: 8, textAlign: "center" }}>
                  —
                </div>
              )}
              {colunas[s]?.map((a) => (
                <div
                  key={a.id}
                  onClick={() => navigate(`/posvenda/assistencias/${a.id}`)}
                  style={{
                    background: "var(--surface, #fff)",
                    border: "1px solid var(--border, #e4e4e7)",
                    borderLeft: a.atrasada
                      ? "4px solid #D32F2F"
                      : `4px solid ${PRIORIDADE_TONES[a.prioridade] === "danger" ? "#D32F2F"
                          : PRIORIDADE_TONES[a.prioridade] === "warning" ? "#F9A825"
                          : PRIORIDADE_TONES[a.prioridade] === "info" ? "#3F88C5"
                          : "#9ca3af"}`,
                    borderRadius: 6,
                    padding: 10,
                    cursor: "pointer",
                    fontSize: 13,
                  }}
                >
                  <div style={{ fontWeight: 700, fontSize: 12 }}>{a.numero}</div>
                  <div style={{
                    margin: "4px 0", fontSize: 13, fontWeight: 600,
                    overflow: "hidden", textOverflow: "ellipsis",
                    display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                  }} title={a.titulo}>
                    {a.titulo}
                  </div>
                  <div style={{ fontSize: 11, opacity: 0.75, marginBottom: 4 }}>
                    {a.cliente_nome}
                  </div>
                  <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                    <Pill $tone={PRIORIDADE_TONES[a.prioridade]} style={{ fontSize: 10 }}>
                      {PRIORIDADE_LABELS[a.prioridade]}
                    </Pill>
                    {a.escalonada && (
                      <Pill $tone="warning" style={{ fontSize: 10 }}>Escalada</Pill>
                    )}
                    {a.atrasada && (
                      <Pill $tone="danger" style={{ fontSize: 10 }}>
                        {formatarHorasRestantes(a.horas_restantes)}
                      </Pill>
                    )}
                  </div>
                  <div style={{ fontSize: 10, opacity: 0.6, marginTop: 4 }}>
                    {fmtData(a.aberta_em)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </PageContainer>
  );
}
